import type { AdminVehicleGenerationDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { GenerationConflictAlert } from './GenerationConflictAlert';
import { GenerationFields } from './GenerationFields';
import { GenerationPreviewLine, GenerationYears } from './GenerationYears';
import { StaleBanner } from './StaleBanner';
import type { UseCatalogWriteResult } from '../_lib/useCatalogWrite';
import { useGenerationForm } from '../_lib/useGenerationForm';
import { useGenerationStatus } from '../_lib/useGenerationStatus';

/**
 * «Добавить поколение» / «Изменить поколение» (AUT-450 / VG-03), after
 * `vehicle-generations.html` (?state=add|edit|toolong|conflict|stale).
 * Saves on its own (POST / PATCH) — never through the model form. `row` is
 * read from the cached list each render (null = add); `items` is the list,
 * for the conflict's holder. A stale 409 → the non-dismissable banner, the
 * list refetched, the typed values kept.
 */
export interface GenerationDialogProps {
  modelId: string;
  modelTitle: string;
  row: AdminVehicleGenerationDto | null;
  items: readonly AdminVehicleGenerationDto[];
  syncing: boolean;
  write: UseCatalogWriteResult;
  onClose: () => void;
}

const K = 'vehicleCatalog.generations';
const FORM_ID = 'vehicle-catalog-generation-form';

const sourceOf = (t: TFunction, row: AdminVehicleGenerationDto): string => {
  if (row.source === 'staff') return t(`${K}.source.staff`);
  return row.staffEdited ? t(`${K}.source.seedEdited`) : t(`${K}.source.seed`);
};

export const GenerationDialog = (p: GenerationDialogProps): ReactElement => {
  const { t } = useTranslation();
  const g = useGenerationForm({
    modelId: p.modelId,
    row: p.row,
    syncing: p.syncing,
    write: p.write,
    onDone: p.onClose,
  });
  const setStatus = useGenerationStatus(p.write);
  const busy = p.write.busy;
  const conflict = g.serverError?.kind === 'generationConflict' ? g.serverError : null;
  const sub = p.row
    ? t(`${K}.dialog.editSub`, { model: p.modelTitle, source: sourceOf(t, p.row) })
    : t(`${K}.dialog.addSub`, { model: p.modelTitle });

  return (
    <Dialog open onClose={busy ? undefined : p.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t(g.isAdd ? `${K}.dialog.addTitle` : `${K}.dialog.editTitle`)}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          {sub}
        </Typography>
        {g.stale ? <StaleBanner /> : null}
        <Box
          component="form"
          id={FORM_ID}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          onSubmit={(e) => void g.form.handleSubmit(g.save)(e)}
        >
          <GenerationFields g={g} disabled={busy} />
          <GenerationYears g={g} disabled={busy} />
        </Box>
        <GenerationPreviewLine g={g} />
        {conflict ? (
          <GenerationConflictAlert
            hit={conflict.hit}
            items={p.items}
            busy={busy || p.syncing}
            onRestore={(holder) =>
              void setStatus(holder, 'active').then((ok) => {
                if (ok) p.onClose();
              })
            }
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={p.onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          disabled={!g.canSave}
          loading={busy}
        >
          {g.isAdd ? t(`${K}.dialog.submitAdd`) : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
